import { defineRule, configure } from 'vee-validate'
import {
  required,
  email,
  numeric,
  max,
  min,
  url,
  mimes,
  image,
} from '@vee-validate/rules'
import { localize, setLocale } from '@vee-validate/i18n'
import deBase from '@vee-validate/i18n/dist/locale/de.json'
import enBase from '@vee-validate/i18n/dist/locale/en.json'
import frBase from '@vee-validate/i18n/dist/locale/fr.json'
import itBase from '@vee-validate/i18n/dist/locale/it.json'
import svBase from '@vee-validate/i18n/dist/locale/sv.json'
import plBase from '@vee-validate/i18n/dist/locale/pl.json'

const customMessages = {
  de: {
    messages: {
      min_files: 'Mindestens {min} Datei(en) sind erforderlich.',
      max_files: 'Es sind maximal {max} Datei(en) zugelassen.',
    },
  },
  en: {
    messages: {
      min_files: 'At least {min} file(s) are required.',
      max_files: 'A maximum of {max} file(s) is allowed.',
    },
  },
  fr: {
    messages: {
      min_files: 'Au moins {min} fichier(s) sont requis.',
      max_files: 'Un maximum de {max} fichier(s) est autorisé.',
    },
  },
  it: {
    messages: {
      min_files: 'Sono richiesti almeno {min} file.',
      max_files: 'È consentito un massimo di {max} file.',
    },
  },
  sv: {
    messages: {
      min_files: 'Minst {min} fil(er) krävs.',
      max_files: 'Högst {max} fil(er) är tillåtna.',
    },
  },
  pl: {
    messages: {
      min_files: 'Wymaganych jest co najmniej {min} plik(ów).',
      max_files: 'Maksymalnie {max} plik(ów) jest dozwolonych.',
    },
  },
}

const mergedLocales = {
  de: {
    ...deBase,
    ...customMessages.de,
    messages: {
      ...deBase.messages,
      ...customMessages.de.messages,
    },
  },
  en: {
    ...enBase,
    ...customMessages.en,
    messages: {
      ...enBase.messages,
      ...customMessages.en.messages,
    },
  },
  fr: {
    ...frBase,
    ...customMessages.fr,
    messages: {
      ...frBase.messages,
      ...customMessages.fr.messages,
    },
  },
  it: {
    ...itBase,
    ...customMessages.it,
    messages: {
      ...itBase.messages,
      ...customMessages.it.messages,
    },
  },
  sv: {
    ...svBase,
    ...customMessages.sv,
    messages: {
      ...svBase.messages,
      ...customMessages.sv.messages,
    },
  },
  pl: {
    ...plBase,
    ...customMessages.pl,
    messages: {
      ...plBase.messages,
      ...customMessages.pl.messages,
    },
  },
}

export default defineNuxtPlugin(({ $i18n }) => {
  defineRule('required', required)
  defineRule('email', email)
  defineRule('numeric', numeric)
  defineRule('integer', numeric)
  defineRule('max', max)
  defineRule('min', min)
  defineRule('url', url)
  defineRule('mimes', mimes)
  defineRule('image', image)

  defineRule('phone', (value) => {
    // Allow empty value (field is optional)
    if (!value || !value.length) {
      return true
    }

    // Swiss & International Numbers
    const regex = /^(?:\+|00)?(0\d{8,11}|\d{9,12})$/
    return regex.test(value)
  })

  // to handle the nullable fields rules from statamic
  defineRule('nullable', () => {
    return true
  })

  defineRule('max_files', (value, [max]) => {
    if (!value) {
      return true // if no file is selected, it's valid
    }

    if (value.length > max) {
      return false
    }

    return true
  })

  defineRule('min_files', (value, [min]) => {
    if (!value) {
      return true // if no file is selected, it's valid
    }

    if (value.length < min) {
      return false
    }

    return true
  })

  configure({
    // controls if `blur` events should trigger validation with `handleChange` handler
    validateOnBlur: true,
    // controls if `change` events should trigger validation with `handleChange` handler
    validateOnChange: true,
    // controls if `input` events should trigger validation with `handleChange` handler
    validateOnInput: true,
    // controls if `update:modelValue` events should trigger validation with `handleChange` handler
    validateOnModelUpdate: true,

    generateMessage: localize({
      // CH Locales
      ch_de: mergedLocales.de,
      ch_fr: mergedLocales.fr,
      ch_it: mergedLocales.it,
      ch_en: mergedLocales.en,

      // AT Locales
      at_de: mergedLocales.de,
      at_en: mergedLocales.en,

      // DE Locales
      de_de: mergedLocales.de,
      de_en: mergedLocales.en,

      // SE Locales
      se_sv: mergedLocales.sv, // Swedish
      se_en: mergedLocales.en,

      // PL Locales
      pl_pl: mergedLocales.pl, // Polish
      pl_en: mergedLocales.en,
    }),
  })

  watch($i18n.locale, (newLocale) => {
    setLocale(newLocale)
  })
  setLocale($i18n.locale.value)
})
